<template>
    <component
        :is="fullscreen ? 'ModalFullscreen' : 'div'"
        v-show="!fullscreen || (fullscreen && fullscreenOpened)"
        @[fullscreen&&`close`]="close"
    >
        <div class="flex flex-col gap-y-5 px-4 lg:px-0 pb-4 lg:pb-0">
            <div class="flex justify-between">
                <div class="flex items-center">
                    <span class="icon icon-filters"></span>
                    <span class="font-display-5">{{ t('search:filter.title') }}</span>
                </div>
                <BtnCta
                    variant="tertiary"
                    :label="t('search:filter.filter_clearance')"
                    compact
                    data-testid="search-page-clear-all-filters"
                    @click="$emit('reset')"
                />
            </div>
            <div class="flex flex-col gap-y-2">
                <span class="font-body-2-medium">{{ t('search:filter.institution_type.title') }}</span>
                <BtnSwitch
                    :options="institutionTypes"
                    :value="institutionType"
                    @input="updateInstitutionType"
                />
            </div>
            <div
                v-show="institutionType === InstitutionType.UNIVERSITY"
                class="flex flex-col gap-y-2"
            >
                <span class="font-body-2-medium">{{ t('search:filter.second_filter.university') }}</span>
                <SearchElastic
                    :model-value="university"
                    class="university-search"
                    data-testid="university-filter_search-bar"
                    :search-context="['universities']"
                    :filters="{ institution_type: 1 }"
                    :placeholder="t('search:filter.university.placeholder')"
                    clear-btn-test-id="university-filter_clear-icon"
                    truncate
                    @input="updateUniversity"
                    @clear="clearUniversity"
                />
            </div>
            <div
                v-show="institutionType === InstitutionType.UNIVERSITY"
                class="flex flex-col gap-y-2"
            >
                <span class="font-body-2-medium">{{ t('search:filter.second_filter.course') }}</span>
                <SearchElastic
                    :model-value="course"
                    :search-context="['courses']"
                    :placeholder="t('search:filter.course.placeholder')"
                    :filters="{ university_id: selectedUniversityId }"
                    data-testid="course-filter_search-bar"
                    clear-btn-test-id="course-filter_clear-icon"
                    truncate
                    @input="updateCourse"
                    @clear="clearCourse"
                />
            </div>
            <div
                v-show="institutionType === InstitutionType.SCHOOL"
                class="flex flex-col gap-y-2"
            >
                <span class="font-body-2-medium">{{ t('search:filter.type_of_school.title') }}</span>
                <SelectInstitution
                    :model-value="school"
                    :placeholder="t('search:filter.type_of_school.placeholder')"
                    without-higher-education-option
                    clearable
                    @input="updateSchool"
                    @clear="clearSchool"
                />
            </div>
            <div
                v-show="institutionType === InstitutionType.SCHOOL"
                class="flex flex-col gap-y-2"
            >
                <span class="font-body-2-medium">{{ t('search:filter.subject_filter.title') }}</span>
                <SelectSubjectSearch
                    :model-value="subject"
                    :university-id="selectedSchool"
                    :placeholder="t('search:filter.subject_filter.placeholder')"
                    clearable
                    @input="updateSubject"
                    @clear="clearSubject"
                />
            </div>
            <div class="flex flex-col gap-y-2">
                <span class="font-body-2-medium">{{ t('search:filter.study_material_type.title') }}</span>
                <div class="flex flex-wrap gap-2">
                    <Chip
                        v-for="(studyMaterialType, index) in availableStudyMaterialTypes"
                        :key="index"
                        :model-value="studyMaterialTypes"
                        :value="studyMaterialType.id"
                        :label="studyMaterialType.name"
                        :data-testid="studyMaterialType.testId"
                        @update:model-value="updateStudyMaterialTypes"
                    />
                </div>
            </div>
            <!---
            TEMPORARY UNTIL MORE AI-DOCS ARE GENERATED
            <div class="flex flex-col gap-y-2">
                <span class="font-body-2-medium">{{ t('tags.aiText') }}</span>
                <InputCheckbox
                    :model-value="aiOnly"
                    @update:model-value="updateAiOnly"
                >
                    <span>{{ t('search:filter.ai_extended.label') }}</span>
                </InputCheckbox>
            </div>
            --->
            <div class="flex flex-col gap-y-2">
                <span class="font-body-2-medium">{{ t('filter.academic_year.title') }}</span>
                <InputRange
                    :model-value="academicYear"
                    :min="ACADEMIC_YEAR_MIN"
                    :max="ACADEMIC_YEAR_MAX"
                    @update:model-value="updateAcademicYear"
                />
            </div>
            <div class="flex flex-col gap-y-2">
                <span class="font-body-2-medium">{{ t('filter.language.title') }}</span>
                <Select
                    :loading="!languages?.length"
                    :model-value="language"
                    :options="languages"
                    :option-groups="languagesHeaders"
                    value-field="id"
                    label-field="name"
                    group-by="is_top"
                    filterable
                    class="w-full text-left col-span-2 md:col-auto z-[6]"
                    @input="updateLanguage"
                />
            </div>
        </div>
    </component>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, type PropType } from 'vue';
import BtnSwitch from '@/components/BtnSwitch';
import SelectInstitution from '@/components/SelectInstitution';
import SelectSubjectSearch from '@/components/SelectSubjectSearch';
import Chip from '@/components/Chip/Chip.vue';
import InputCheckbox from '@/components/InputCheckbox';
import InputRange from '@/components/InputRange';
import type {
    UniversityBaseResource as UniversityResource,
    InstitutionResource as SchoolResource,
} from '@/api/backend/resources/UniversityResource';
import type { SearchCourseResource } from '@/api/gateway/legacy-api/resources/SearchCourseResource';
import { StudyMaterialTypeId, ACADEMIC_YEAR_MIN, ACADEMIC_YEAR_MAX } from '@/utils/searchUtils';
import { InstitutionType } from '@/api/gateway/legacy-api/resources/UniversityResource';
import type { LanguageResource } from '@/api/gateway/legacy-api/resources/LanguageResource';
import { useTranslation } from 'i18next-vue';

type CourseResource = Pick<SearchCourseResource, 'id' | 'name'>;
/**
 * This component represents the search filters. Filters are wrapped
 * by the ModalFullscreen component in small and medium sized screens.
 */
export default defineComponent({
    name: 'SearchFilters',
    components: {
        ModalFullscreen: defineAsyncComponent(() => import('@/components/ModalFullscreen')),
        BtnSwitch,
        SelectInstitution,
        SelectSubjectSearch,
        Chip,
        InputCheckbox,
        InputRange,
    },
    props: {
        /**
         * Education Type represents school and university filters.
         */
        institutionType: {
            type: Number as PropType<InstitutionType>,
            required: true,
        },
        /**
         * University filter.
         */
        university: {
            type: Object as PropType<UniversityResource>,
            required: true,
            default: undefined,
        },
        /**
         * Course filter.
         */
        course: {
            type: Object as PropType<CourseResource>,
            required: true,
            default: undefined,
        },
        /**
         * School type filter.
         */
        school: {
            type: Object as PropType<SchoolResource>,
            required: true,
            default: undefined,
        },
        /**
         * School subject filter.
         */
        subject: {
            type: Object as PropType<CourseResource>,
            required: true,
            default: undefined,
        },
        /**
         * Document type filter.
         */
        studyMaterialTypes: {
            type: Array as PropType<StudyMaterialTypeId[]>,
            required: true,
        },
        /**
         * AI content filter.
         */
        aiOnly: {
            type: Boolean,
            required: true,
        },
        /**
         * Semester filter.
         */
        academicYear: {
            type: Array as PropType<number[]>,
            required: true,
        },
        /**
         * Whether or not to show the filters in fullscreen modal.
         */
        fullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * To pop up the fullscreen modal. Has effect only when 'fullscreen' property is true.
         */
        fullscreenOpened: {
            type: Boolean,
            default: false,
        },
        /** list of all languages */
        languages: {
            type: Array as PropType<LanguageResource[]>,
            required: true,
        },
        /**
         * Language object
         */
        language: {
            type: Object as PropType<LanguageResource>,
            default: undefined,
        },
    },
    emits: {
        'update:institution-type': (institutionType: InstitutionType) => true, // eslint-disable-line
        'update:university': (university: UniversityResource | undefined) => true, // eslint-disable-line
        'update:course': (course: CourseResource | undefined) => true, // eslint-disable-line
        'update:school': (school: SchoolResource | undefined) => true, // eslint-disable-line
        'update:subject': (subject: CourseResource | undefined) => true, // eslint-disable-line
        'update:study-material-types': (studyMaterialTypes: StudyMaterialTypeId[]) => true, // eslint-disable-line
        'update:ai-only': (aiOnly: Boolean) => true, // eslint-disable-line
        'update:language': (language: LanguageResource | undefined) => true, // eslint-disable-line
        'update:academic-year': (academicYear: number[]) => true, // eslint-disable-line
        'update:fullscreen-opened': (fullscreenStatus: boolean) => true, // eslint-disable-line
        reset: () => true,
    },
    setup() {
        const { t } = useTranslation(['search', 'upload']);
        return {
            InstitutionType,
            ACADEMIC_YEAR_MIN,
            ACADEMIC_YEAR_MAX,
            t,
        };
    },
    computed: {
        languagesHeaders() {
            return [
                { id: true, label: this.t('upload:file_selected.languages_common') },
                { id: false, label: this.t('upload:file_selected.languages_all') },
            ];
        },
        availableStudyMaterialTypes() {
            return [
                {
                    id: StudyMaterialTypeId.Assignments,
                    name: this.t('filter.document_types.assignments'),
                    testId: 'study-material-type_assignments',
                },
                {
                    id: StudyMaterialTypeId.Exams,
                    name: this.t('filter.document_types.exams'),
                    testId: 'study-material-type_exams',
                },
                {
                    id: StudyMaterialTypeId.Flashcards,
                    name: this.t('filter.document_types.flashcards'),
                    testId: 'study-material-type_flashcards',
                },
                {
                    id: StudyMaterialTypeId.Lectures,
                    name: this.t('filter.document_types.lectures'),
                    testId: 'study-material-type_lectures',
                },
                {
                    id: StudyMaterialTypeId.Other,
                    name: this.t('filter.document_types.other'),
                    testId: 'study-material-type_other',
                },
                {
                    id: StudyMaterialTypeId.Summaries,
                    name: this.t('filter.document_types.summaries'),
                    testId: 'study-material-type_summaries',
                },
            ] as Array<{ id: StudyMaterialTypeId; name: string; testId: string }>;
        },
        institutionTypes(): Array<{ label: string; value: InstitutionType; testId: string }> {
            return [
                {
                    label: this.t('university'),
                    value: InstitutionType.UNIVERSITY,
                    testId: 'filter-education-type_University',
                },
                {
                    label: this.t('search:filter.highschool'),
                    value: InstitutionType.SCHOOL,
                    testId: 'filter-education-type_High-school',
                },
            ];
        },
        selectedUniversityId(): number | undefined {
            return this.university?.id;
        },
        selectedSchool(): number | undefined {
            return this.school?.id;
        },
    },
    watch: {
        course(newCourse: SearchCourseResource | undefined): void {
            if (!this.university && newCourse) {
                this.updateUniversity({
                    id: newCourse.university_id,
                    name: newCourse.university_name,
                });
            }
        },
    },
    methods: {
        updateInstitutionType(institutionType: InstitutionType): void {
            this.$emit('update:institution-type', institutionType);
        },
        updateUniversity(university: UniversityResource | undefined): void {
            this.$emit('update:university', university);
        },
        updateCourse(course: CourseResource | undefined): void {
            this.$emit('update:course', course);
        },
        updateLanguage(languageId: number | undefined): void {
            this.$emit(
                'update:language',
                this.languages.find((l) => l.id === languageId),
            );
        },
        updateSchool(school: SchoolResource | undefined): void {
            this.$emit('update:school', school);
        },
        updateSubject(subject: CourseResource | undefined): void {
            this.$emit('update:subject', subject);
        },
        updateStudyMaterialTypes(studyMaterialTypes: StudyMaterialTypeId[]): void {
            this.$emit('update:study-material-types', studyMaterialTypes);
        },
        updateAiOnly(aiOnly: boolean): void {
            this.$emit('update:ai-only', aiOnly);
        },
        updateAcademicYear(academicYear: number[]): void {
            this.$emit('update:academic-year', academicYear);
        },
        clearUniversity(): void {
            this.updateUniversity(undefined);
        },
        clearCourse(): void {
            this.updateCourse(undefined);
        },
        clearSchool(): void {
            this.updateSchool(undefined);
            this.updateSubject(undefined);
        },
        clearSubject(): void {
            this.updateSubject(undefined);
        },
        close(): void {
            this.$emit('update:fullscreen-opened', false);
        },
    },
});
</script>

<style lang="scss" scoped>
:deep(.vue-slider-dot) {
    @apply z-[4];
}
</style>
